'use client';

import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';
import { Accordion as MoleculeAccordion } from '@/shared-ui/molecules/accordion';
import { cn } from '@/lib/utils';

type LinkColumnNavProps = Omit<ComponentPropsWithRef<'nav'>, 'children'>;

export type LinkColumnTitleOptions = {
    title?: string;
    titleVariant?: TypographyProps['variant'];
    titleWeight?: TypographyProps['weight'];
};

export type LinkColumnListProps = ComponentPropsWithRef<'ul'> & {
    themeClassName?: string;
};

const List = ({ className, themeClassName, ref, ...props }: LinkColumnListProps) => (
    <ul
        ref={ref}
        data-slot="link-column-list"
        className={cn('m-0 flex list-none flex-col gap-3 p-0', themeClassName, className)}
        {...props}
    />
);
List.displayName = 'LinkColumn.List';

type LinkColumnBaseColumnProps = LinkColumnNavProps & {
    /**
     * Theme / colour utilities for the column shell (e.g. `text-secondary` on `bg-primary`).
     * Applied to the `nav`, title, accordion trigger, and list shell — pass the same utility on link
     * `Button` `className` when using `variant="link"` (`text-*` overrides the default `text-primary`).
     */
    themeClassName?: string;
    children?: ReactNode;
};

export type LinkColumnListColumnProps = LinkColumnBaseColumnProps & LinkColumnTitleOptions;

const ListColumn = ({
    ref,
    className,
    themeClassName,
    title,
    titleVariant = 'h4',
    titleWeight = 'regular',
    children,
    ...navProps
}: LinkColumnListColumnProps) => {
    const titleId = useId();
    const trimmedTitle = title?.trim();
    const navAriaLabelledBy = trimmedTitle ? titleId : undefined;

    return (
        <nav
            ref={ref}
            data-slot="link-column"
            data-variant="list"
            aria-labelledby={navAriaLabelledBy}
            className={cn('flex w-full flex-col gap-4 text-left', themeClassName, className)}
            {...navProps}>
            {trimmedTitle ? (
                <Typography
                    data-slot="link-column-heading"
                    variant={titleVariant}
                    weight={titleWeight}
                    id={titleId}
                    className={cn(themeClassName)}>
                    {trimmedTitle}
                </Typography>
            ) : null}
            {children ? <List themeClassName={themeClassName}>{children}</List> : null}
        </nav>
    );
};
ListColumn.displayName = 'LinkColumn.ListColumn';

export type LinkColumnAccordionColumnProps = LinkColumnBaseColumnProps & {
    accordionValue: string;
    defaultOpen?: boolean;
    title?: string;
    titleVariant?: TypographyProps['variant'];
    titleWeight?: TypographyProps['weight'];
    accordionContentClassName?: string;
};

const AccordionColumn = ({
    ref,
    className,
    themeClassName,
    title,
    titleVariant = 'h4',
    titleWeight = 'regular',
    accordionValue,
    defaultOpen = false,
    children,
    accordionContentClassName,
    ...navProps
}: LinkColumnAccordionColumnProps) => {
    const titleId = useId();

    if (!title?.trim()) {
        return null;
    }

    return (
        <nav
            ref={ref}
            data-slot="link-column"
            data-variant="accordion"
            aria-labelledby={titleId}
            className={cn('w-full', themeClassName, className)}
            {...navProps}>
            <MoleculeAccordion.Root className="w-full" defaultValue={defaultOpen ? [accordionValue] : undefined}>
                <MoleculeAccordion.Item value={accordionValue}>
                    <MoleculeAccordion.Trigger
                        headingId={titleId}
                        variant={titleVariant}
                        weight={titleWeight}
                        className={cn('py-4', themeClassName)}>
                        {title}
                    </MoleculeAccordion.Trigger>
                    <MoleculeAccordion.Content accordionContentClassName={accordionContentClassName}>
                        {children ? <List themeClassName={themeClassName}>{children}</List> : null}
                    </MoleculeAccordion.Content>
                </MoleculeAccordion.Item>
            </MoleculeAccordion.Root>
        </nav>
    );
};
AccordionColumn.displayName = 'LinkColumn.AccordionColumn';

export const LinkColumn = {
    List,
    ListColumn,
    AccordionColumn,
} as const;
